import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/contexts/AuthContext';

export interface DashboardStats {
  totalRevenue: number;
  revenueChange: number;
  totalOrders: number;
  ordersChange: number;
  activeSubscriptions: number;
  subscriptionsChange: number;
  apiCallsMonth: number;
  apiCallsChange: number;
}

export interface RevenueDataPoint {
  month: string;
  revenue: number;
  orders: number;
}

export interface TopProduct {
  name: string;
  revenue: number;
  sales: number;
  change: number;
}

export interface ActivityItem {
  id: string;
  message: string;
  time: string;
  icon: string;
  color: string;
}

export interface DashboardData {
  stats: DashboardStats;
  revenueData: RevenueDataPoint[];
  topProducts: TopProduct[];
  recentActivity: ActivityItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function useDashboardStats(): DashboardData {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalRevenue: 0, revenueChange: 0,
    totalOrders: 0, ordersChange: 0,
    activeSubscriptions: 0, subscriptionsChange: 0,
    apiCallsMonth: 0, apiCallsChange: 0,
  });
  const [revenueData, setRevenueData] = useState<RevenueDataPoint[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);

  const fetchData = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    setError(null);

    try {
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59).toISOString();

      const [
        { data: allOrders },
        { data: activeSubs },
        { data: profile },
      ] = await Promise.all([
        supabase.from('orders').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('subscriptions').select('id, status, created_at, product_name, requests_used, requests_limit, pricing_type, end_date').eq('user_id', user.id),
        supabase.from('profiles').select('api_calls_month, api_calls_total').eq('id', user.id).maybeSingle(),
      ]);

      const orders = allOrders ?? [];
      const subscriptions = activeSubs ?? [];

      // Revenue from paid orders (more reliable than payments table which has webhook user_id issues)
      const finishedPayments = orders.filter((o: { status: string }) =>
        o.status === 'paid' || o.status === 'finished' || o.status === 'confirmed'
      );
      const totalRevenue = finishedPayments.reduce((s: number, p: { amount: string | number }) => s + Number(p.amount), 0);

      // This month vs last month revenue
      const thisMonthRev = finishedPayments
        .filter((p: { created_at: string }) => p.created_at >= thisMonthStart)
        .reduce((s: number, p: { amount: string | number }) => s + Number(p.amount), 0);
      const lastMonthRev = finishedPayments
        .filter((p: { created_at: string }) => p.created_at >= lastMonthStart && p.created_at <= lastMonthEnd)
        .reduce((s: number, p: { amount: string | number }) => s + Number(p.amount), 0);
      const revenueChange = lastMonthRev > 0 ? Math.round(((thisMonthRev - lastMonthRev) / lastMonthRev) * 100) : 0;

      // Orders this month vs last
      const thisMonthOrders = orders.filter((o: { created_at: string }) => o.created_at >= thisMonthStart).length;
      const lastMonthOrders = orders.filter((o: { created_at: string }) => o.created_at >= lastMonthStart && o.created_at <= lastMonthEnd).length;
      const ordersChange = lastMonthOrders > 0 ? Math.round(((thisMonthOrders - lastMonthOrders) / lastMonthOrders) * 100) : 0;

      // Subscriptions
      const activeSubCount = subscriptions.filter((s: { status: string }) => s.status === 'active').length;
      const thisMonthSubs = subscriptions.filter((s: { created_at: string }) => s.created_at >= thisMonthStart).length;
      const lastMonthSubs = subscriptions.filter((s: { created_at: string }) => s.created_at >= lastMonthStart && s.created_at <= lastMonthEnd).length;
      const subsChange = lastMonthSubs > 0 ? Math.round(((thisMonthSubs - lastMonthSubs) / lastMonthSubs) * 100) : 0;

      // API calls from profile
      const apiCallsMonth = (profile as { api_calls_month?: number } | null)?.api_calls_month ?? 0;

      // Revenue chart - last 6 months
      const chartData: RevenueDataPoint[] = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextD = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
        const monthLabel = `${MONTH_LABELS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
        const monthRev = finishedPayments
          .filter((p: { created_at: string }) => p.created_at >= d.toISOString() && p.created_at < nextD.toISOString())
          .reduce((s: number, p: { amount: string | number }) => s + Number(p.amount), 0);
        const monthOrders = orders.filter((o: { created_at: string }) =>
          o.created_at >= d.toISOString() && o.created_at < nextD.toISOString()
        ).length;
        chartData.push({ month: monthLabel, revenue: Math.round(monthRev), orders: monthOrders });
      }

      // Top products from orders
      const productMap = new Map<string, { revenue: number; sales: number }>();
      orders
        .filter((o: { status: string }) => o.status === 'paid')
        .forEach((o: { product_name: string; amount: string | number }) => {
          const existing = productMap.get(o.product_name) ?? { revenue: 0, sales: 0 };
          productMap.set(o.product_name, {
            revenue: existing.revenue + Number(o.amount),
            sales: existing.sales + 1,
          });
        });
      const top: TopProduct[] = Array.from(productMap.entries())
        .sort(([, a], [, b]) => b.revenue - a.revenue)
        .slice(0, 5)
        .map(([name, data]) => ({ name, revenue: Math.round(data.revenue), sales: data.sales, change: 0 }));

      // Recent activity from orders
      const activityItems: ActivityItem[] = orders.slice(0, 6).map((o: {
        id: string; product_name: string; status: string; amount: string | number; created_at: string; pricing_type: string;
      }) => {
        const isPaid = o.status === 'paid';
        const isFailed = o.status === 'failed';
        return {
          id: o.id,
          message: isPaid
            ? `Payment confirmed for ${o.product_name} (${o.pricing_type?.replace(/_/g, '-')}) — $${Number(o.amount).toFixed(2)}`
            : isFailed
            ? `Payment failed for order ${o.id} — ${o.product_name}`
            : `Order ${o.id} is ${o.status} — ${o.product_name}`,
          time: timeAgo(o.created_at),
          icon: isPaid ? 'ri-checkbox-circle-fill' : isFailed ? 'ri-error-warning-fill' : 'ri-time-fill',
          color: isPaid ? 'text-green-500' : isFailed ? 'text-red-500' : 'text-amber-500',
        };
      });

      setStats({
        totalRevenue: Math.round(totalRevenue),
        revenueChange,
        totalOrders: orders.length,
        ordersChange,
        activeSubscriptions: activeSubCount,
        subscriptionsChange: subsChange,
        apiCallsMonth,
        apiCallsChange: 0,
      });
      setRevenueData(chartData);
      setTopProducts(top);
      setRecentActivity(activityItems);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { stats, revenueData, topProducts, recentActivity, loading, error, refetch: fetchData };
}
